import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Eye, TrendingUp, TrendingDown, AlertTriangle, CheckCircle2, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmt = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);

const KIND_THEME = {
  good:    { label: 'BONNE AFFAIRE',     bg: 'from-emerald-50 to-emerald-100', border: 'border-emerald-400', text: 'text-emerald-700', bar: 'bg-emerald-500',  Icon: CheckCircle2 },
  viable:  { label: 'OPÉRATION VIABLE',  bg: 'from-amber-50 to-amber-100',     border: 'border-amber-400',   text: 'text-amber-700',   bar: 'bg-amber-500',    Icon: TrendingUp },
  risky:   { label: 'À NÉGOCIER',        bg: 'from-rose-50 to-rose-100',       border: 'border-rose-400',    text: 'text-rose-700',    bar: 'bg-rose-500',     Icon: AlertTriangle },
};

const VERDICT_THEME = {
  undervalued: { Icon: TrendingUp,   text: 'text-emerald-700' },
  overvalued:  { Icon: TrendingDown, text: 'text-rose-700' },
  aligned:     { Icon: CheckCircle2, text: 'text-amber-700' },
  neutral:     { Icon: Eye,          text: 'text-blue-700' },
};

export default function ExpertVerdictGauge({ projectId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/projects/${projectId}/system-estimation`)
      .then(res => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setData({ available: false }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [projectId]);

  if (loading) {
    return (
      <div className="rounded-xl border-2 border-navy/10 bg-gray-50 p-4 animate-pulse" data-testid="expert-verdict-loading">
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-3" />
        <div className="h-8 bg-gray-200 rounded w-2/3 mb-2" />
        <div className="h-2 bg-gray-200 rounded w-full" />
      </div>
    );
  }

  if (!data || !data.available) return null;

  const kind = data.avis_kind || 'viable';
  const theme = KIND_THEME[kind] || KIND_THEME.viable;
  const Icon = theme.Icon;
  const margePct = Number(data.marge_pct || 0);
  // Jauge : -10% (rouge) → 0% → +15% (vert) → +30% (max)
  const gaugePct = Math.max(0, Math.min(100, ((margePct + 10) / 40) * 100));

  const verdictKind = data.verdict_kind || 'neutral';
  const verdictTheme = VERDICT_THEME[verdictKind] || VERDICT_THEME.neutral;
  const VerdictIcon = verdictTheme.Icon;

  return (
    <div className={`rounded-xl border-2 ${theme.border} bg-gradient-to-br ${theme.bg} p-5 shadow-sm`} data-testid={`expert-verdict-${projectId}`}>
      {/* Header avec verdict */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-navy" />
          <h4 className="text-xs font-bold tracking-wider text-navy uppercase">Œil de l'Expert</h4>
        </div>
        <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/60 ${theme.text}`}>
          <Icon className="w-4 h-4" />
          <span className="text-xs font-bold">{theme.label}</span>
        </div>
      </div>

      {/* Marge nette en gros */}
      <div className="mb-4">
        <p className="text-xs text-navy/60 mb-1">Marge nette potentielle (après tous frais)</p>
        <div className="flex items-baseline gap-3">
          <span className={`text-3xl font-bold ${theme.text}`} data-testid="marge-nette-amount">
            {data.marge_nette >= 0 ? '+' : ''}{fmt(data.marge_nette)}
          </span>
          <span className={`text-base font-semibold ${theme.text}`}>
            {margePct >= 0 ? '+' : ''}{margePct.toFixed(1)}%
          </span>
        </div>
      </div>

      {/* Jauge */}
      <div className="mb-4">
        <div className="relative h-3 bg-white/60 rounded-full overflow-hidden">
          {/* Zones de fond : rouge / orange / vert */}
          <div className="absolute inset-0 flex">
            <div className="bg-rose-200/60" style={{ width: '25%' }} />
            <div className="bg-amber-200/60" style={{ width: '37.5%' }} />
            <div className="bg-emerald-200/60" style={{ width: '37.5%' }} />
          </div>
          {/* Curseur */}
          <div
            className={`absolute top-0 bottom-0 w-1 ${theme.bar} shadow-md`}
            style={{ left: `calc(${gaugePct}% - 2px)` }}
          />
        </div>
        <div className="flex justify-between text-[10px] text-navy/50 mt-1 font-medium">
          <span>Risqué (-10%)</span>
          <span>Viable (+5%)</span>
          <span>Bonne affaire (+15%)</span>
          <span>+30%</span>
        </div>
      </div>

      {/* Détails calcul */}
      <div className="grid grid-cols-2 gap-2 text-xs mb-3">
        <div className="bg-white/60 rounded-lg px-3 py-2">
          <p className="text-navy/60">Valeur cible revente</p>
          <p className="font-bold text-navy">{fmt(data.valeur_cible_revente)}</p>
        </div>
        <div className="bg-white/60 rounded-lg px-3 py-2">
          <p className="text-navy/60">Estimation client</p>
          <p className="font-bold text-navy">
            {data.client_resale_price > 0 ? fmt(data.client_resale_price) : '— non saisie —'}
          </p>
        </div>
      </div>

      {/* Verdict comparaison */}
      <div className="flex items-start gap-2 bg-white/70 rounded-lg p-3">
        <VerdictIcon className={`w-5 h-5 ${verdictTheme.text} flex-shrink-0 mt-0.5`} />
        <p className="text-xs leading-relaxed text-navy">{data.verdict_label}</p>
      </div>

      {/* Source */}
      <p className="text-[10px] text-navy/40 mt-3 italic">
        Source : DVF data.gouv.fr ({data.nb_transactions || 0} ventes analysées) · Coef caractéristiques : {data.features_coefficient >= 0 ? '+' : ''}{data.features_coefficient}% · Valorisation travaux 70%
      </p>
    </div>
  );
}
